import {
  Alert,
  IconButton,
  Paper,
  Slide,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DailyMonthlyPerformanceTabs from "./DailyMonthlyPerformance/DailyMonthlyPerformanceTabs";
import "./BrandConfigurationOverview.css";
import AdvisorSettings from "./AdvisorSettings/AdvisorSettings";
import FetcherSettings from "./FetcherSettings/FetcherSettings";
import UnifierSettings from "./UnifierSettings/UnifierSettings";
import { useEffect, useState } from "react";
import LastRuns from "./RunModals/LastRuns";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BrandConfigOverviewSkeleton from "../../BrandSkeletons/BrandConfigOverviewSkeleton";
import { useCopyToClipboard } from "../../brandPropertiesHook/useCopyToClipboard";
import { selectUserToken } from "../../../../../features/userSlice";
import { BrandConfigReportEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import {
  setBrandConfigOverviewLoader,
  setFetchedTabs,
  setIsFetchedBrandConfigOverviewData,
  setIsSuccess,
  setModalOpen,
  setProcessFailure,
} from "../../../../../features/brandConfigurationReportSlice";
import {
  formatToUTC,
  QUERY_IDS,
  REPORT_QUERY_ID_MAP,
} from "../../utilitiesOfBrandConfig/helperOfBrandConfig";
import RunBrandData from "./RunModals/RunBrandData/RunBrandData";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import StatusChip from "../../utilitiesOfBrandConfig/StatusChip";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";
import ObjectsSection from "./ObjectsSection/ObjectsSection";

// Keywords Table date
const tableHeadings = {
  process_name: "Process Name",
  range: "Range",
  status: "Status",
  step: "Step",
  started_at: "Run Start",
  ended_at: "Run End",
};

const BrandConfigurationOverview = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams("");
  const [lastRunLoader, setLastRunLoader] = useState(false);
  const brandIdFromURL = searchParams.get("brand_id") || ""; // Get brand_id from URL query parameters
  const reportFromURL = searchParams.get("report") || "daily_user"; // Get report type from URL query parameters (default to "daily_user" if not provided)
  const start_date = searchParams.get("start_date") || null; // Get start_date from URL query parameters
  const end_date = searchParams.get("end_date") || null;
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [openLastRunModal, setOpenLastRunModal] = useState(false);

  const { isModalOpen, fetcherConfigData } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );
  const { userData } = useSelector((state: any) => state.user);
  const type = "site";

  const QUERY_ID = "5652708983832576";

  // Get 1st website_shared_uid from website_permissions based on current requirements
  const website_shared_uid = useWebsiteSharedUid();

  const { copied, copy } = useCopyToClipboard(); // Custom hook for clipboard copy

  const isFetchedBrandConfigOverviewData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.isFetchedBrandConfigOverviewData,
  );
  const { isSuccess, isProcessFailed, processFailureMessage, resWorkflowName } =
    useSelector((state: any) => state.brandConfigurationReport);
  // fetch brand data
  const brandOverviewInfoData = useSelector(
    (state: any) => state.brandConfigurationReport.brandOverviewInfoData,
  );
  // fetch last run data
  const brandOverviewLastRunData = useSelector(
    (state: any) => state.brandConfigurationReport.brandOverviewLastRunData,
  );

  const handleLastRunModal = () => {
    setOpenLastRunModal(true);
  };
  const handleCloseModal = () => {
    setOpenLastRunModal(false);
  };

  // Fetch the overview data when the component mounts
  useEffect(() => {
    const fetchBrandConfigOverviewData = async (
      brandIdFromURL: string | any,
      reportFromURL: string | any,
    ) => {
      if (!brandIdFromURL?.trim() || !reportFromURL || !website_shared_uid)
        return;

      const hasUrlDates = !!start_date && !!end_date;

      dispatch(setBrandConfigOverviewLoader(true));
      dispatch(setIsFetchedBrandConfigOverviewData(false));
      try {
        await Promise.all([
          BrandConfigReportEndPointLogic.getBrandOverviewInfo(
            oauthTokenHeader,
            brandIdFromURL,
          ),
          // BrandConfigReportEndPointLogic.getBrandOverviewLastRecords(
          //   oauthTokenHeader,
          //   brandIdFromURL,
          //   reportFromURL,
          // ),
          BrandConfigReportEndPointLogic.getOverviewPerformance(
            oauthTokenHeader,
            reportFromURL,
            REPORT_QUERY_ID_MAP[reportFromURL] ??
              QUERY_IDS.DAILY_USER_PERFORMANCE,
            website_shared_uid,
            type,
            brandIdFromURL,
            hasUrlDates ? start_date : null, // ← null on fresh load
            hasUrlDates ? end_date : null, // ← null on fresh load
            hasUrlDates ? 0 : 10,
          ),

          BrandConfigReportEndPointLogic.getBrandOverviewAdvisorSettings(
            oauthTokenHeader,
            brandIdFromURL,
            reportFromURL,
          ),
          BrandConfigReportEndPointLogic.getObjectsData(
            oauthTokenHeader,
            QUERY_IDS.OBJECTS_SECTION,
            website_shared_uid,
            type,
            brandIdFromURL,
          ),
          BrandConfigReportEndPointLogic.getAllConfigForFetcher(
            oauthTokenHeader,
            QUERY_ID,
            website_shared_uid,
          ),
        ]);

        const response =
          await BrandConfigReportEndPointLogic.getBrandOverviewUnifierSettings(
            oauthTokenHeader,
            brandIdFromURL,
            reportFromURL,
          );

        // fetch unique raw table ids from Unifier settings
        const rawTableIds =
          Array.isArray(response) && response.length > 0
            ? [
                ...new Set(
                  response.map((item: any) => Number(item.raw_table_id)),
                ),
              ]
            : [];

        const parsedBrandId = parseInt(brandIdFromURL, 10);
        const parsedRawTableIds = rawTableIds
          .map((id: any) => parseInt(id, 10))
          .filter((id) => !isNaN(id)); // drop any non-numeric ids
        // Guard: skip fetcher call if either value is unusable
        if (!isNaN(parsedBrandId) && parsedRawTableIds.length > 0) {
          const requestBodyForFetcher = {
            brand_id: parsedBrandId,
            raw_table_id: parsedRawTableIds,
          };
          await BrandConfigReportEndPointLogic.getBrandOverviewFetcherSettings(
            oauthTokenHeader,
            requestBodyForFetcher,
            reportFromURL,
          );
        }

        // //  Mark the initial tab as fetched so the tab effect skips it
        dispatch(setFetchedTabs(reportFromURL as any));
      } catch (error) {
        console.error(
          "Error fetching brand configuration overview data:",
          error,
        );
      } finally {
        dispatch(setBrandConfigOverviewLoader(false));
        dispatch(setIsFetchedBrandConfigOverviewData(true));
      }
      // eslint-disable-next-line
    };

    // Fetch last run data (Separately called as ts take too much time to load)
    const fetchLastRunDataForAllTabs = async (
      brandIdFromURL: string | any,
      reportFromURL: string | any,
    ) => {
      if (!brandIdFromURL?.trim() || !reportFromURL) return;
      setLastRunLoader(true);
      try {
        await BrandConfigReportEndPointLogic.getBrandOverviewLastRuns(
          oauthTokenHeader,
          brandIdFromURL,
        );
      } catch (error) {
        console.error("Error fetching last run data for all tabs:", error);
      } finally {
        setLastRunLoader(false);
      }
    };

    fetchBrandConfigOverviewData(brandIdFromURL, reportFromURL);
    // Fetch last run data (seperately called as ts take too much time to load)
    fetchLastRunDataForAllTabs(brandIdFromURL, reportFromURL);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setIsSuccess(false));
    dispatch(setProcessFailure({ isFailed: false, message: "" }));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    dispatch(setIsSuccess(false));
    dispatch(setProcessFailure({ isFailed: false, message: "" }));
  };

  const handleProcessLogsNavigation = () => {
    const today = new Date().toISOString().split("T")[0];
    const query = new URLSearchParams({
      start_date: today,
      end_date: today,
      workflow_name: resWorkflowName,
      page: "0",
    });
    window.open(`/data_collection/data_logs?${query.toString()}`, "_blank");
  };

  const brandId = brandOverviewInfoData?.brand_id;
  const hasViewMonitoringPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "monitoring",
    "view_monitoring",
  );
  const hasViewWorkflowPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "monitoring",
    "view_workflows",
  );
  const hasPermissionForClick: any =
    (hasViewMonitoringPermission && hasViewWorkflowPermission) || false;
  const row_output_table_data = Array.from(
    new Map(
      (fetcherConfigData?.query_output || [])
        .filter((item: any) => item.brand_id === brandId)
        .map((item: any) => [
          item.raw_output_table_name, // unique key (or config_id if safer)
          {
            config_name: item.config_name,
            table_id: item.raw_output_table_name,
          },
        ]),
    ).values(),
  );

  // Get the latest record based on started_at
  const latestRecord = [...brandOverviewLastRunData]
    //.filter((record) => record.ended_at != null) // ← exclude null/undefined
    .sort(
      (a, b) => new Date(b.ended_at).getTime() - new Date(a.ended_at).getTime(),
    )[0];

  // Extract the latest status and last run
  const latestStatus = latestRecord?.status ?? "";
  const lastRun = latestRecord?.ended_at ?? "";

  return (
    <>
      <Slide
        direction="left"
        in={isSuccess}
        mountOnEnter
        unmountOnExit
        timeout={200}
      >
        <div>
          <Alert
            severity="success"
            onClose={handleClose}
            sx={{
              mb: 2,
              border: "1px solid rgba(0, 128, 0, 0.3)",
              backgroundColor: "rgba(240, 255, 240, 0.9)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "17px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              The process has been launched successfully!
            </Typography>
            {hasPermissionForClick && (
              <>
                To monitor the workflow results{" "}
                <span
                  onClick={handleProcessLogsNavigation}
                  style={{
                    textDecoration: "underline",
                    color: "#9C27B0",
                    cursor: "pointer",
                  }}
                  data-tracker="790"
                >
                  CLICK HERE
                </span>{" "}
                (It may take a few minutes for the logs to appear in the Process
                Log Table.)
              </>
            )}
          </Alert>
        </div>
      </Slide>
      <Slide
        direction="left"
        in={isProcessFailed}
        mountOnEnter
        unmountOnExit
        timeout={200}
      >
        <div>
          <Alert
            severity="error"
            onClose={handleClose}
            sx={{
              mb: 2,
              border: "1px solid rgba(211, 47, 47, 0.35)",
              backgroundColor: "rgba(255, 235, 238, 0.9)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "17px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              The process request had failed
            </Typography>
            {processFailureMessage || "Unknown error occurred"}
          </Alert>
        </div>
      </Slide>
      {isFetchedBrandConfigOverviewData ? (
        <>
          <Paper>
            <Grid container>
              <Grid size={3} p={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  {brandOverviewInfoData?.brand_name && (
                    <Typography sx={{}} variant="h5">
                      {brandOverviewInfoData?.brand_name}
                    </Typography>
                  )}

                  <Typography
                    component="span"
                    onClick={() =>
                      dispatch(
                        setModalOpen({
                          isOpen: true,
                          data: {
                            brandName: brandOverviewInfoData?.brand_name,
                            brandId: brandOverviewInfoData?.brand_id,
                            row_output_table_data,
                          },
                        }),
                      )
                    }
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      color: "#9C27B0",
                      fontSize: "14px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      ml: 1,
                      lineHeight: 1,
                      textDecoration: "underline",
                    }}
                  >
                    Run Brand
                    <PlayCircleFilledIcon sx={{ fontSize: 15 }} />
                  </Typography>

                  <RunBrandData
                    open={isModalOpen}
                    //handleClose={handleCloseRunBrandModal}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  {brandOverviewInfoData?.company_name && (
                    <Typography variant="body2">
                      Company: <b>{brandOverviewInfoData?.company_name}</b>
                    </Typography>
                  )}
                  {brandOverviewInfoData?.brand_id && (
                    <Typography variant="body2">
                      Brand ID: <b>{brandOverviewInfoData?.brand_id}</b>
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid
                size={9}
                pt={1}
                pb={2}
                px={2}
                display={"flex"}
                gap={1}
                justifyContent={"flex-end"}
              >
                <Paper
                  sx={{
                    p: 1,
                    boxShadow: "none",
                    border: "1px solid #0000001F",
                    width: "194px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", color: "#00000099", mb: 2 }}
                  >
                    Affiliate System
                  </Typography>
                  {brandOverviewInfoData?.affiliate_system && (
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body2">
                        {brandOverviewInfoData?.affiliate_system
                          ? brandOverviewInfoData?.affiliate_system
                          : ""}
                      </Typography>
                      <RouterLink
                        style={{ color: "#9C27B0" }}
                        to={brandOverviewInfoData?.login_url}
                        target="_blank"
                      >
                        <IconButton
                          data-tracker="785"
                          aria-label="delete"
                          size="small"
                          color="secondary"
                        >
                          <OpenInNewIcon fontSize="inherit" />
                        </IconButton>
                      </RouterLink>
                    </Box>
                  )}
                </Paper>

                <Paper
                  sx={{
                    p: 1,
                    boxShadow: "none",
                    border: "1px solid #0000001F",
                    width: "194px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", color: "#00000099", mb: 2.3 }}
                  >
                    Account Name
                  </Typography>
                  {brandOverviewInfoData?.account_username && (
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body2">
                        {brandOverviewInfoData?.account_username}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                <Paper
                  sx={{
                    p: 1,
                    boxShadow: "none",
                    border: "1px solid #0000001F",
                    width: "194px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", color: "#00000099", mb: 2 }}
                  >
                    Keeper ID
                  </Typography>
                  {brandOverviewInfoData?.keeper_id && (
                    <Box
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Tooltip
                        title={brandOverviewInfoData?.keeper_id}
                        placement="bottom"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "130px",
                          }}
                        >
                          {brandOverviewInfoData?.keeper_id}
                        </Typography>
                      </Tooltip>

                      <Tooltip
                        title={copied ? "Copied!" : "Copy"}
                        placement="top"
                        arrow
                      >
                        <IconButton
                          data-tracker="786"
                          aria-label="copy keeper id"
                          size="small"
                          color={copied ? "success" : "secondary"}
                          onClick={() => copy(brandOverviewInfoData?.keeper_id)}
                        >
                          {copied ? (
                            <CheckCircleOutlinedIcon fontSize="inherit" />
                          ) : (
                            <ContentCopyIcon fontSize="inherit" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Paper>

                <Paper
                  sx={{
                    px: 1,
                    py: 0.5,
                    boxShadow: "none",
                    border: "1px solid #0000001F",
                    width: "320px",
                  }}
                >
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    sx={{ mb: 1.2 }}
                  >
                    {lastRunLoader ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          color: "#00000099",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        Last Run
                        <IconButton size="small" disabled sx={{ ml: 0.5 }}>
                          <AccessTimeIcon
                            fontSize="inherit"
                            sx={{
                              animation: "spinFade 1.4s ease-in-out infinite",
                              "@keyframes spinFade": {
                                "0%, 100%": {
                                  opacity: 0.35,
                                  transform: "translateY(2px) rotate(0deg)",
                                },
                                "50%": {
                                  opacity: 1,
                                  transform: "translateY(-2px) rotate(180deg)",
                                },
                                "100%": {
                                  transform: "translateY(2px) rotate(360deg)",
                                },
                              },
                            }}
                          />
                        </IconButton>
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px", color: "#00000099" }}
                      >
                        {Array.isArray(brandOverviewLastRunData) &&
                        brandOverviewLastRunData?.length > 0
                          ? "Last Run"
                          : "No Runs Yet"}
                        <IconButton
                          data-tracker="789"
                          aria-label="delete"
                          size="small"
                          color="secondary"
                          sx={{ ml: 0.5 }}
                          disabled={
                            !Array.isArray(brandOverviewLastRunData) ||
                            brandOverviewLastRunData?.length === 0
                          } // Disable if there's no last run data
                          onClick={handleLastRunModal}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                        <LastRuns
                          open={openLastRunModal}
                          handleClose={handleCloseModal}
                          tableHeadings={tableHeadings}
                        />
                      </Typography>
                    )}

                    <StatusChip status={latestStatus} />
                  </Box>
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"end"}
                  >
                    <Typography variant="body2">
                      {lastRun ? (
                        <>
                          <b style={{ fontWeight: 500 }}>Last Run:</b>{" "}
                          {formatToUTC(lastRun)}
                        </>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* DailyMonthlyPerformance Tabs */}
            <DailyMonthlyPerformanceTabs />

            {/* Advisor Settings */}
            <AdvisorSettings />

            {/* Unifier Settings */}
            <UnifierSettings />

            {/* Fetcher Settings */}
            <FetcherSettings />

            {/* Objects Section */}
            <ObjectsSection />
          </Paper>
        </>
      ) : (
        <>
          <BrandConfigOverviewSkeleton />
        </>
      )}
    </>
  );
};

export default BrandConfigurationOverview;
